import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Eye, Edit, Trash2, ImageIcon, Lock } from 'lucide-react';

import { formatDistanceToNow } from 'date-fns';

import { TableCell, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { getUnitLabel } from '@/types/unit-types';
import EntityActions from '@/components/shared/EntityActions';
import { formatCurrency, cn } from '@/lib/utils';
import { supabase } from '@/integrations/supabase/client';
import { HighlightMatch } from '@/components/internal/line-items/HighlightMatch';

interface LineItem {
  id: string;
  item_type?: 'internal' | 'system';
  name?: string;
  title?: string;
  description?: string;
  base_unit?: string;
  unit_symbol?: string;
  final_price?: number | null;
  base_cost?: number;
  category?: string | null;
  category_name?: string | null;
  trade?: string | null;
  trade_name?: string | null;
  tags?: string[] | string | null;
  image_urls?: string[];
  is_active?: boolean;
  price_last_updated_at?: string | null;
  updated_at?: string;
}

interface LineItemTableRowProps {
  item: LineItem;
  onRowClick: (item: LineItem) => void;
  onDelete: (id: string) => void;
  searchQuery?: string;
}

const LineItemTableRow: React.FC<LineItemTableRowProps> = ({
  item,
  onRowClick,
  onDelete,
  searchQuery = '',
}) => {
  const navigate = useNavigate();

  const { data: staleThresholds } = useQuery({
    queryKey: ['platform_stale_thresholds'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('get_platform_stale_thresholds');
      if (error) throw error;
      return data as { amber_days: number; red_days: number };
    },
    staleTime: 5 * 60 * 1000,
  });
  const amberDays = staleThresholds?.amber_days ?? 90;
  const redDays   = staleThresholds?.red_days   ?? 180;

  const handleEdit = (id: string) => {
    navigate(`/cost-center/internal-line-items/edit/${id}`);
  };

  const getLineItemActions = () => [
    { label: "View", icon: Eye, onClick: () => onRowClick(item) },
    { label: "Edit", icon: Edit, onClick: () => handleEdit(item.id) },
    { 
      label: "Delete", 
      icon: Trash2, 
      onClick: () => onDelete(item.id),
      destructive: true,
      requiresConfirmation: {
        title: "Delete Line Item?",
        description: `Are you sure you want to delete "${item.title || 'this item'}"? This action cannot be undone.`,
      },
    },
  ];

  const renderTags = (tags?: string[] | string) => {
    if (!tags) return null;
    
    let tagsArray: string[] = [];
    
    if (Array.isArray(tags)) {
      tagsArray = tags;
    } else if (typeof tags === 'string') {
      try {
        const parsed = JSON.parse(tags);
        if (Array.isArray(parsed)) {
          tagsArray = parsed;
        } else {
          tagsArray = [tags];
        }
      } catch (e) {
        tagsArray = [tags];
      }
    }
    
    return (
      <div className="flex flex-wrap gap-1">
        {tagsArray.map((tag, index) => (
          <Badge key={index} variant="outline">{tag}</Badge>
        ))}
      </div>
    );
  };

  return (
    <TableRow 
      className="cursor-pointer hover:bg-muted/60"
      onClick={() => onRowClick(item)}
    >
      <TableCell className="w-12 hidden sm:table-cell overflow-hidden">
        {item.image_urls && item.image_urls.length > 0 ? (
          <img 
            src={item.image_urls[0]} 
            alt={item.title || 'Line item'} 
            className="h-8 w-8 rounded object-cover"
          />
        ) : (
          <div className="h-8 w-8 rounded bg-muted flex items-center justify-center">
            <ImageIcon className="h-4 w-4 text-muted-foreground" />
          </div>
        )}
      </TableCell>
      <TableCell className="max-w-0 w-full">
        <div className="min-w-0">
          <span className="font-medium block truncate">
            <HighlightMatch text={item.name || item.title || 'No title'} query={searchQuery} />
          </span>
          <p className="text-sm text-muted-foreground truncate">
            <HighlightMatch text={item.description || 'No description'} query={searchQuery} />
          </p>
        </div>
      </TableCell>
      <TableCell className="hidden sm:table-cell w-32 overflow-hidden">
        <span className="block truncate text-sm">
          {item.unit_symbol || getUnitLabel(item.base_unit || 'item')}
        </span>
      </TableCell>
      <TableCell className="w-36 font-mono overflow-hidden">
        <div className="truncate">
          {item.final_price !== null && item.final_price !== undefined ? (
            formatCurrency(item.final_price)
          ) : (
            <span className="text-muted-foreground italic text-xs">N/A</span>
          )}
        </div>
        {(() => {
          const timestamp = item.price_last_updated_at || item.updated_at;
          if (!timestamp) return null;
          const ageMs = Date.now() - new Date(timestamp).getTime();
          const colour = ageMs > redDays * 24 * 60 * 60 * 1000
            ? 'text-red-600'
            : ageMs > amberDays * 24 * 60 * 60 * 1000
            ? 'text-amber-600'
            : 'text-muted-foreground';
          return (
            <div className={cn(
              'text-[10px] mt-0.5 truncate max-w-full',
              colour
            )}>
              {formatDistanceToNow(new Date(timestamp), { addSuffix: true })}
            </div>
          );
        })()}
      </TableCell>
      <TableCell className="hidden lg:table-cell w-44 overflow-hidden">
        <div className="truncate">
          {item.category_name ? (
            <Badge variant="outline" className="text-xs">{item.category_name}</Badge>
          ) : item.category ? (
            <Badge variant="outline" className="text-xs">{item.category}</Badge>
          ) : (
            <span className="text-muted-foreground text-xs">—</span>
          )}
        </div>
      </TableCell>
      <TableCell className="hidden lg:table-cell w-24 overflow-hidden">
        {item.item_type === 'system' ? (
          <Badge className="bg-teal-100 text-teal-800 border-transparent hover:bg-teal-100 text-xs">
            System
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-xs">
            Internal
          </Badge>
        )}
      </TableCell>
      <TableCell className="text-right w-20 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {item.item_type === 'system' ? (
          <Lock className="h-4 w-4 text-muted-foreground inline-block" />
        ) : (
          <EntityActions actions={getLineItemActions()} size="sm" />
        )}
      </TableCell>
    </TableRow>
  );
};

export default LineItemTableRow;
